import dayjs, { type Dayjs } from "dayjs";
import type {
  AgeAggregation,
  AgeCategory,
  AgeCategoryTable,
  FormationSize,
} from "./queries/rules";

/**
 * Client-side mirror of internal/service/rules/age.go for live feedback
 * in the entry form. The server stays the authority — these produce
 * hints, never decisions. A change here must change there too.
 */

/** Competition age of someone born on `birth`, per the table's mode. */
export function ageOn(
  birth: string | Dayjs,
  table: Pick<AgeCategoryTable, "age_mode" | "ref_month" | "ref_day">,
  competitionDate: string | Dayjs,
): number {
  const b = dayjs(birth);
  const comp = dayjs(competitionDate);
  const ref =
    table.age_mode === "fixed_date"
      ? dayjs(new Date(comp.year(), table.ref_month - 1, table.ref_day))
      : comp;
  let years = ref.year() - b.year();
  if (ref.month() < b.month() || (ref.month() === b.month() && ref.date() < b.date())) {
    years--;
  }
  return years;
}

/** Folds individual ages per the formation rule. The mean stays exact
 * (14.5 is 14.5, and displayed as such) — the band bounds are
 * whole-year intervals, so the boundary check absorbs the fraction,
 * not a rounding step here. Mirrors rules.AggregateAge in Go. */
export function aggregateAge(ages: number[], agg: AgeAggregation): number {
  if (ages.length === 0) return 0;
  switch (agg) {
    case "oldest":
      return Math.max(...ages);
    case "average":
      return ages.reduce((a, b) => a + b, 0) / ages.length;
    default:
      return ages[0];
  }
}

/* A band bound is a whole year of life, not an integer point: you are
 * "14" until the moment you turn 15, so max_age 14 covers everything
 * below 15.00 and min_age 15 starts exactly there. The bands therefore
 * tile the age axis with no gaps, and a fractional average (14.5) has
 * a natural home instead of falling between 14 and 15. */

/** Band check; missing bounds are open ends. This is the NATURAL
 * category — where the age belongs by itself. */
export function ageCategoryMatches(
  cat: Pick<AgeCategory, "min_age" | "max_age">,
  age: number,
): boolean {
  if (cat.min_age != null && age < cat.min_age) return false;
  if (cat.max_age != null && age >= cat.max_age + 1) return false;
  return true;
}

/** Whether the age may compete in the category at all: only the upper
 * bound binds — younger competitors may start in an older category
 * (announced in the UI), older ones may never start down. Mirrors
 * rules.AgeCategoryAdmits. */
export function ageCategoryAdmits(
  cat: Pick<AgeCategory, "min_age" | "max_age">,
  age: number,
): boolean {
  return cat.max_age == null || age < cat.max_age + 1;
}

/** Whether the headcount fits the formation band. */
export function headcountFits(
  size: Pick<FormationSize, "min_count" | "max_count">,
  n: number,
): boolean {
  if (n < size.min_count) return false;
  if (size.max_count != null && n > size.max_count) return false;
  return true;
}

/** Czech description of a formation band ("2–3", "min. 8", "přesně 1"). */
export function headcountBound(
  size: Pick<FormationSize, "min_count" | "max_count">,
): string {
  if (size.max_count == null) return `min. ${size.min_count}`;
  if (size.max_count === size.min_count) return `přesně ${size.min_count}`;
  return `${size.min_count}–${size.max_count}`;
}

/** Renders an aggregated age without fake precision (13, or 13,5). */
export function formatAge(age: number): string {
  if (Number.isInteger(age)) return String(age);
  return age.toFixed(1).replace(".", ",");
}

/**
 * The Czech label of the reference date the ages are computed against —
 * "k 31. 12. 2027" for a fixed-date table (the competition's calendar
 * year), or "v den soutěže (25. 4. 2027)" for at-competition rules.
 * Shown wherever a computed age appears, so nobody has to guess which
 * date the number means.
 */
export function ageReferenceLabel(
  table: Pick<AgeCategoryTable, "age_mode" | "ref_month" | "ref_day">,
  competitionDate: string | Dayjs,
): string {
  const comp = dayjs(competitionDate);
  if (table.age_mode === "fixed_date") {
    return `k ${table.ref_day}. ${table.ref_month}. ${comp.year()}`;
  }
  return `v den soutěže (${comp.format("D. M. YYYY")})`;
}
